import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Button,
  Flex,
  HStack,
  VStack,
  Text,
  Icon,
  Badge,
  Skeleton,
  SkeletonText,
  Tag,
  TagLabel,
  TagLeftIcon,
  Divider,
  useToast,
} from "@chakra-ui/react";
import {
  IoBriefcaseOutline,
  IoCardOutline,
  IoCheckmarkCircle,
  IoAlertCircleOutline,
  IoOpenOutline,
  IoRefreshOutline,
  IoTimeOutline,
  IoWalletOutline,
  IoBusinessOutline,
  IoMailOutline,
  IoGlobeOutline,
  IoCashOutline,
  IoArrowForward,
  IoLockClosedOutline,
} from "react-icons/io5";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/firebase-config";
import { getStripeMode, getStripePublishableKey } from "../../lib/stripeMode";

// Stripe amounts come in the smallest currency unit (cents). USD/CAD/EUR/GBP
// are the only currencies we expect today; default to 2 decimals for anything
// else and let `Intl` do the heavy lifting.
const formatMoney = (amount, currency) => {
  if (typeof amount !== "number") return "—";
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency || "CAD",
      minimumFractionDigits: 2,
    }).format(amount / 100);
  } catch (_) {
    return `$${(amount / 100).toFixed(2)}`;
  }
};

const maskStripeKey = (key) => {
  if (!key) return "missing";
  return `${key.slice(0, 7)}...${key.slice(-4)}`;
};

const titleCase = (s) =>
  (s || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase())
    .trim();

const describePayoutSchedule = (schedule) => {
  if (!schedule) return "Not set";
  const interval = (schedule.interval || "").toLowerCase();
  const delay =
    typeof schedule.delayDays === "number" ? `${schedule.delayDays}-day delay` : null;
  if (interval === "manual") return ["Manual payouts", delay].filter(Boolean).join(" • ");
  if (interval === "daily") return ["Daily", delay].filter(Boolean).join(" • ");
  if (interval === "weekly") {
    return [
      "Weekly",
      schedule.weeklyAnchor && `every ${titleCase(schedule.weeklyAnchor)}`,
      delay,
    ]
      .filter(Boolean)
      .join(" • ");
  }
  if (interval === "monthly") {
    return [
      "Monthly",
      schedule.monthlyAnchor && `on the ${schedule.monthlyAnchor}${ordinalSuffix(schedule.monthlyAnchor)}`,
      delay,
    ]
      .filter(Boolean)
      .join(" • ");
  }
  return [titleCase(interval || "—"), delay].filter(Boolean).join(" • ");
};

const ordinalSuffix = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return "";
  if (v % 100 >= 11 && v % 100 <= 13) return "th";
  switch (v % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

// Small KV row used inside the info card.
const InfoRow = ({ icon, label, value, mono, isLoading }) => (
  <HStack align="flex-start" spacing={3} py={2}>
    <Flex
      minW="32px"
      h="32px"
      borderRadius="lg"
      bg="gray.50"
      border="1px solid"
      borderColor="gray.100"
      align="center"
      justify="center"
      color="gray.500"
      flexShrink={0}
    >
      <Icon as={icon} boxSize="14px" />
    </Flex>
    <VStack align="flex-start" spacing={0} flex={1} minW={0}>
      <Text
        fontSize="11px"
        fontWeight="700"
        color="gray.400"
        textTransform="uppercase"
        letterSpacing="0.06em"
      >
        {label}
      </Text>
      {isLoading ? (
        <Skeleton h="14px" w="120px" mt="4px" />
      ) : (
        <Text
          fontSize="13px"
          fontWeight="600"
          color="gray.800"
          fontFamily={mono ? "mono" : undefined}
          noOfLines={1}
          wordBreak="break-all"
          w="full"
        >
          {value || "—"}
        </Text>
      )}
    </VStack>
  </HStack>
);

const StatusPill = ({ ok, label }) => (
  <Tag
    size="sm"
    variant="subtle"
    colorScheme={ok ? "green" : "orange"}
    borderRadius="full"
    px={3}
    py={1}
  >
    <TagLeftIcon
      boxSize="12px"
      as={ok ? IoCheckmarkCircle : IoAlertCircleOutline}
    />
    <TagLabel fontSize="11px" fontWeight="700">
      {label}
    </TagLabel>
  </Tag>
);

export const HostSettingsModal = ({ isOpen, onClose, user }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (silent = false) => {
      if (!user) return;
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const stripeMode = getStripeMode();
        const publishableKey = getStripePublishableKey();
        console.log("[HostSettings] Stripe config", {
          mode: stripeMode,
          publishableKey: maskStripeKey(publishableKey),
          keyType: publishableKey.startsWith("pk_live_")
            ? "live publishable"
            : publishableKey.startsWith("pk_test_")
            ? "test publishable"
            : "unknown/missing",
        });
        const callable = httpsCallable(functions, "getHostStripeAccount");
        const { data: result } = await callable({ mode: stripeMode });
        console.log("[HostSettings] Stripe account response", {
          requestedMode: stripeMode,
          resolvedMode: result?.mode,
          accountId: result?.accountId,
          country: result?.country,
          defaultCurrency: result?.defaultCurrency,
        });
        setData(result || null);
      } catch (err) {
        console.error("❌ Failed to load host Stripe account:", err);
        setError(err.message || "Failed to load Stripe account info.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [user]
  );

  useEffect(() => {
    if (isOpen) load(false);
    // Reset on close so we always re-fetch fresh on next open.
    if (!isOpen) {
      setData(null);
      setError(null);
    }
  }, [isOpen, load]);

  const acct = data || {};
  const cap = acct.capabilities || {};
  const allOk = cap.chargesEnabled && cap.payoutsEnabled && cap.detailsSubmitted;

  const externalAccountLabel = useMemo(() => {
    const ea = acct.externalAccount;
    if (!ea) return null;
    if (ea.type === "bank_account") {
      return [ea.bankName, ea.last4 && `•••• ${ea.last4}`, ea.currency]
        .filter(Boolean)
        .join(" · ");
    }
    if (ea.type === "card") {
      return [
        titleCase(ea.brand || ""),
        ea.last4 && `•••• ${ea.last4}`,
        ea.expMonth && ea.expYear && `exp ${String(ea.expMonth).padStart(2, "0")}/${String(ea.expYear).slice(-2)}`,
      ]
        .filter(Boolean)
        .join(" · ");
    }
    return null;
  }, [acct.externalAccount]);

  const handleOpenDashboard = () => {
    if (!acct.dashboardUrl) {
      toast({
        title: "Dashboard not available yet",
        description:
          "Finish your Stripe onboarding to access the full Express dashboard.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    window.open(acct.dashboardUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "full", md: "2xl" }}
      isCentered
      scrollBehavior="inside"
      motionPreset="slideInBottom"
    >
      <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.500" />
      <ModalContent
        borderRadius={{ base: 0, md: "2xl" }}
        overflow="hidden"
        my={{ base: 0, md: 8 }}
      >
        <ModalHeader
          pb={3}
          pt={5}
          px={{ base: 5, md: 6 }}
          borderBottom="1px solid"
          borderColor="gray.100"
          bg="white"
          position="sticky"
          top={0}
          zIndex={2}
        >
          <Flex align="center" justify="space-between" gap={3}>
            <HStack spacing={3} align="center" minW={0}>
              <Box
                bg="blue.50"
                p={2}
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={IoBriefcaseOutline} color="#508cf7" boxSize={4} />
              </Box>
              <Box minW={0}>
                <Text fontSize="lg" fontWeight="700" color="gray.800">
                  Host Settings
                </Text>
                <Text fontSize="xs" color="gray.400" noOfLines={1}>
                  Your Stripe payout & business info
                </Text>
              </Box>
            </HStack>
            {/* Reserve room on the right so the mode badge never tucks
                under the close (X) button. */}
            {acct.mode && (
              <Badge
                fontSize="10px"
                borderRadius="full"
                px={2}
                py="2px"
                mr={10}
                textTransform="uppercase"
                letterSpacing="0.06em"
                colorScheme={acct.mode === "live" ? "green" : "purple"}
                variant="subtle"
                flexShrink={0}
              >
                {acct.mode === "live" ? "Live" : "Test"}
              </Badge>
            )}
          </Flex>
          <ModalCloseButton top={4} right={4} />
        </ModalHeader>

        <ModalBody px={{ base: 5, md: 6 }} py={{ base: 5, md: 6 }}>
          {/* Error state ─────────────────────────────────────────── */}
          {error && (
            <HStack
              spacing={3}
              bg="red.50"
              border="1px solid"
              borderColor="red.100"
              borderRadius="lg"
              px={3}
              py={3}
              align="flex-start"
              mb={4}
            >
              <Icon as={IoAlertCircleOutline} color="red.500" boxSize={4} mt="2px" />
              <Box>
                <Text fontSize="xs" color="red.700" fontWeight="700">
                  Couldn't load your Stripe account
                </Text>
                <Text fontSize="11px" color="red.700" mt="2px">
                  {error}
                </Text>
              </Box>
            </HStack>
          )}

          {/* No-account state ────────────────────────────────────── */}
          {!loading && data && data.hasAccount === false && (
            <VStack
              spacing={4}
              align="center"
              textAlign="center"
              py={{ base: 6, md: 10 }}
              px={4}
            >
              <Flex
                w="56px"
                h="56px"
                borderRadius="full"
                bg="blue.50"
                align="center"
                justify="center"
              >
                <Icon as={IoLockClosedOutline} color="#508cf7" boxSize={6} />
              </Flex>
              <Box>
                <Text fontSize="md" fontWeight="700" color="gray.800">
                  No Stripe account yet
                </Text>
                <Text fontSize="sm" color="gray.500" mt={1} maxW="380px">
                  You haven't connected a Stripe account yet. Once you do,
                  payout details and balance will appear here.
                </Text>
              </Box>
            </VStack>
          )}

          {/* Loaded state ────────────────────────────────────────── */}
          {(loading || (data && data.hasAccount)) && (
            <VStack spacing={4} align="stretch">
              {/* Hero: status + payout destination */}
              <Box
                bgGradient="linear(to-br, blue.50, white)"
                border="1px solid"
                borderColor="blue.100"
                borderRadius="xl"
                p={4}
              >
                <Flex
                  justify="space-between"
                  align="flex-start"
                  gap={3}
                  mb={3}
                  flexWrap="wrap"
                >
                  <Box minW={0}>
                    <HStack spacing={2} align="center">
                      <Text
                        fontSize="11px"
                        fontWeight="700"
                        color="gray.500"
                        textTransform="uppercase"
                        letterSpacing="0.06em"
                      >
                        Account status
                      </Text>
                      <Button
                        size="xs"
                        variant="ghost"
                        color="gray.500"
                        h="22px"
                        minW="22px"
                        px={1.5}
                        borderRadius="md"
                        leftIcon={<Icon as={IoRefreshOutline} boxSize={3} />}
                        onClick={() => load(true)}
                        isLoading={refreshing}
                        _hover={{ bg: "blue.100", color: "#226cf1" }}
                        aria-label="Refresh account info"
                      >
                        <Text fontSize="10px" fontWeight="700">
                          Refresh
                        </Text>
                      </Button>
                    </HStack>
                    {loading ? (
                      <Skeleton h="22px" w="180px" mt={2} />
                    ) : (
                      <HStack mt={1.5} spacing={2} flexWrap="wrap">
                        {allOk ? (
                          <StatusPill ok label="Active" />
                        ) : (
                          <StatusPill ok={false} label="Action needed" />
                        )}
                        {!loading && (
                          <>
                            <StatusPill
                              ok={cap.chargesEnabled}
                              label={cap.chargesEnabled ? "Charges on" : "Charges off"}
                            />
                            <StatusPill
                              ok={cap.payoutsEnabled}
                              label={cap.payoutsEnabled ? "Payouts on" : "Payouts off"}
                            />
                          </>
                        )}
                      </HStack>
                    )}
                  </Box>
                  <Button
                    size="sm"
                    bg="#508cf7"
                    color="white"
                    fontWeight="600"
                    borderRadius="lg"
                    rightIcon={<Icon as={IoOpenOutline} boxSize={4} />}
                    _hover={{ bg: "#226cf1" }}
                    onClick={handleOpenDashboard}
                    isDisabled={loading || !acct.dashboardUrl}
                  >
                    Stripe Dashboard
                  </Button>
                </Flex>

                <Divider borderColor="blue.100" my={3} />

                <HStack spacing={3} align="center">
                  <Flex
                    minW="38px"
                    h="38px"
                    borderRadius="lg"
                    bg="white"
                    border="1px solid"
                    borderColor="blue.100"
                    align="center"
                    justify="center"
                    color="#508cf7"
                  >
                    <Icon
                      as={
                        acct.externalAccount?.type === "card"
                          ? IoCardOutline
                          : IoWalletOutline
                      }
                      boxSize={4}
                    />
                  </Flex>
                  <Box flex={1} minW={0}>
                    <Text
                      fontSize="10px"
                      fontWeight="700"
                      color="gray.400"
                      textTransform="uppercase"
                      letterSpacing="0.06em"
                    >
                      Payout destination
                    </Text>
                    {loading ? (
                      <Skeleton h="14px" w="200px" mt={1} />
                    ) : (
                      <Text
                        fontSize="13px"
                        fontWeight="700"
                        color="gray.800"
                        noOfLines={1}
                      >
                        {externalAccountLabel ||
                          "No payout destination on file"}
                      </Text>
                    )}
                    {!loading && acct.externalAccountCount > 1 && (
                      <Text fontSize="11px" color="gray.400">
                        +{acct.externalAccountCount - 1} other on file
                      </Text>
                    )}
                  </Box>
                </HStack>
              </Box>

              {/* Balance */}
              <Box
                border="1px solid"
                borderColor="gray.100"
                borderRadius="xl"
                p={4}
              >
                <Text
                  fontSize="11px"
                  fontWeight="700"
                  color="gray.400"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                  mb={3}
                >
                  Balance
                </Text>
                <HStack spacing={3} flexWrap="wrap">
                  <Box
                    flex="1"
                    minW="160px"
                    bg="green.50"
                    border="1px solid"
                    borderColor="green.100"
                    borderRadius="lg"
                    p={3}
                  >
                    <HStack spacing={2} color="green.700" mb={1}>
                      <Icon as={IoCashOutline} boxSize={3.5} />
                      <Text
                        fontSize="10px"
                        fontWeight="700"
                        textTransform="uppercase"
                        letterSpacing="0.06em"
                      >
                        Available
                      </Text>
                    </HStack>
                    {loading ? (
                      <Skeleton h="22px" w="100px" />
                    ) : acct.balance?.available?.length ? (
                      <VStack align="stretch" spacing={0}>
                        {acct.balance.available.map((b) => (
                          <Text
                            key={`av-${b.currency}`}
                            fontSize="lg"
                            fontWeight="800"
                            color="green.800"
                          >
                            {formatMoney(b.amount, b.currency)}
                          </Text>
                        ))}
                      </VStack>
                    ) : (
                      <Text fontSize="sm" color="green.700">
                        $0.00
                      </Text>
                    )}
                  </Box>

                  <Box
                    flex="1"
                    minW="160px"
                    bg="orange.50"
                    border="1px solid"
                    borderColor="orange.100"
                    borderRadius="lg"
                    p={3}
                  >
                    <HStack spacing={2} color="orange.700" mb={1}>
                      <Icon as={IoTimeOutline} boxSize={3.5} />
                      <Text
                        fontSize="10px"
                        fontWeight="700"
                        textTransform="uppercase"
                        letterSpacing="0.06em"
                      >
                        Pending
                      </Text>
                    </HStack>
                    {loading ? (
                      <Skeleton h="22px" w="100px" />
                    ) : acct.balance?.pending?.length ? (
                      <VStack align="stretch" spacing={0}>
                        {acct.balance.pending.map((b) => (
                          <Text
                            key={`pn-${b.currency}`}
                            fontSize="lg"
                            fontWeight="800"
                            color="orange.800"
                          >
                            {formatMoney(b.amount, b.currency)}
                          </Text>
                        ))}
                      </VStack>
                    ) : (
                      <Text fontSize="sm" color="orange.700">
                        $0.00
                      </Text>
                    )}
                  </Box>
                </HStack>

                <HStack mt={3} spacing={2} color="gray.500">
                  <Icon as={IoTimeOutline} boxSize={3.5} />
                  <Text fontSize="11px">
                    Payout schedule:{" "}
                    <Text as="span" color="gray.700" fontWeight="600">
                      {loading ? "—" : describePayoutSchedule(acct.payoutSchedule)}
                    </Text>
                  </Text>
                </HStack>
              </Box>

              {/* Outstanding requirements (if any) */}
              {!loading &&
                acct.requirements &&
                ((acct.requirements.currentlyDue || []).length > 0 ||
                  acct.requirements.disabledReason) && (
                  <Box
                    bg="orange.50"
                    border="1px solid"
                    borderColor="orange.200"
                    borderRadius="xl"
                    p={4}
                  >
                    <HStack spacing={2} mb={2}>
                      <Icon
                        as={IoAlertCircleOutline}
                        color="orange.500"
                        boxSize={4}
                      />
                      <Text
                        fontSize="13px"
                        fontWeight="700"
                        color="orange.800"
                      >
                        Stripe needs a few more details
                      </Text>
                    </HStack>
                    {acct.requirements.disabledReason && (
                      <Text fontSize="11px" color="orange.700" mb={2}>
                        Reason: {titleCase(acct.requirements.disabledReason)}
                      </Text>
                    )}
                    {(acct.requirements.currentlyDue || []).length > 0 && (
                      <Box mb={2}>
                        <Text
                          fontSize="10px"
                          fontWeight="700"
                          color="orange.700"
                          textTransform="uppercase"
                          letterSpacing="0.06em"
                          mb={1}
                        >
                          Currently due
                        </Text>
                        <Flex gap={1.5} flexWrap="wrap">
                          {acct.requirements.currentlyDue
                            .slice(0, 12)
                            .map((req) => (
                              <Badge
                                key={req}
                                variant="outline"
                                colorScheme="orange"
                                borderRadius="full"
                                px={2}
                                py="2px"
                                fontSize="10px"
                                fontWeight="600"
                                textTransform="none"
                              >
                                {titleCase(req)}
                              </Badge>
                            ))}
                        </Flex>
                      </Box>
                    )}
                    <Button
                      size="xs"
                      variant="solid"
                      bg="orange.500"
                      color="white"
                      mt={2}
                      _hover={{ bg: "orange.600" }}
                      rightIcon={<Icon as={IoArrowForward} boxSize={3} />}
                      onClick={handleOpenDashboard}
                      isDisabled={!acct.dashboardUrl}
                    >
                      Resolve in Stripe
                    </Button>
                  </Box>
                )}

              {/* Account info card */}
              <Box
                border="1px solid"
                borderColor="gray.100"
                borderRadius="xl"
                p={2}
              >
                <Box px={2} pt={2}>
                  <Text
                    fontSize="11px"
                    fontWeight="700"
                    color="gray.400"
                    textTransform="uppercase"
                    letterSpacing="0.06em"
                  >
                    Account
                  </Text>
                </Box>
                <Box>
                  <InfoRow
                    icon={IoMailOutline}
                    label="Stripe email"
                    value={acct.email}
                    isLoading={loading}
                  />
                  <Divider borderColor="gray.50" />
                  <InfoRow
                    icon={IoBusinessOutline}
                    label="Business name"
                    value={acct.businessProfile?.name}
                    isLoading={loading}
                  />
                  <Divider borderColor="gray.50" />
                  <InfoRow
                    icon={IoGlobeOutline}
                    label="Country / currency"
                    value={
                      acct.country || acct.defaultCurrency
                        ? `${acct.country || "—"} · ${acct.defaultCurrency || "—"}`
                        : null
                    }
                    isLoading={loading}
                  />
                  <Divider borderColor="gray.50" />
                  <InfoRow
                    icon={IoBriefcaseOutline}
                    label="Business type"
                    value={titleCase(acct.businessType)}
                    isLoading={loading}
                  />
                  <Divider borderColor="gray.50" />
                  <InfoRow
                    icon={IoLockClosedOutline}
                    label="Account ID"
                    value={acct.accountId}
                    mono
                    isLoading={loading}
                  />
                </Box>
              </Box>

              {loading && (
                <Box>
                  <SkeletonText noOfLines={3} spacing={2} skeletonHeight="3" />
                </Box>
              )}

              <Text fontSize="11px" color="gray.400" textAlign="center" pt={1}>
                Need help? Email{" "}
                <Text
                  as="a"
                  href="mailto:arpan@proximy.ca?subject=Host%20Settings%20%E2%80%94%20Stripe%20Help"
                  fontWeight="600"
                  color="gray.600"
                  textDecoration="underline"
                  _hover={{ color: "#226cf1" }}
                >
                  arpan@proximy.ca
                </Text>
                .
              </Text>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
